import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { sortBy } from "lodash";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import {
  Modal,
  ModalHeader,
  ModalProps,
} from "@sonder/ui/source/styled/components/Modal";
import { GenericDropdownItem } from "@sonder/ui/source/styled/fragments/GenericDropdownItem";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Input } from "@sonder/ui/source/styled/fragments/Input";
import { InputField } from "@sonder/ui/source/styled/fragments/InputField";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import { ModalFooter } from "@sonder/ui/source/styled/fragments/Modal";
import { UserCardInDropdownItem } from "@sonder/ui/source/styled/fragments/UserCardInDropdownItem";
import { sizes } from "@sonder/ui/source/styled/theme";
import { useStore as useAuthStore } from "auth/store";
import { useDebounce } from "core/hooks/useDebounce";
import { useRolesBasedGetCleaningCompanies } from "core/queries/GetCleaningCompanies";
import { UserData, useSearchForUsers } from "core/queries/SearchForUsers";
import AddQualityAuditConfigurationButton from "./AddQualitityAuditConfigurationButton";

const FormLabel = styled(Label)`
  margin-bottom: 0.625rem;
`;

const FormInput = styled(Input)`
  @media screen and (min-width: ${sizes.minBreakpoint}) {
    min-width: 20rem;
  }
`;

const FieldContainer = styled.div`
  margin-bottom: 0.625rem;
`;

interface QualityAuditsEmailConfigurationModal
  extends Omit<ModalProps, "children"> {}

interface Company {
  id: string;
}

export default function QualityAuditsEmailConfigurationModal({
  isOpen,
  onClose,
}: QualityAuditsEmailConfigurationModal) {
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>(
    undefined,
  );
  const [selectedUser, setSelectedUser] = useState<UserData | undefined>(
    undefined,
  );

  const {
    state: { user: loggedInUser },
  } = useAuthStore();

  const cityId = loggedInUser?.cityId;

  const { data: companyData } = useRolesBasedGetCleaningCompanies({
    skip: !cityId,
    variables: {
      filters: {
        active: true,
        cityId: cityId || "",
      },
    },
  });

  const allCompanies = sortBy(companyData?.cleaningCompany || [], "name");
  const filteredCompanies = companySearchTerm
    ? allCompanies.filter(cleaningCompany =>
        cleaningCompany.name
          .toLowerCase()
          .includes(companySearchTerm.toLocaleLowerCase()),
      )
    : allCompanies;

  const debouncedSearchTerm = useDebounce(userSearchTerm, 300);

  const { data, loading, error } = useSearchForUsers({
    skip: userSearchTerm === "",
    variables: {
      searchTerm: debouncedSearchTerm,
    },
  });

  const handleSelectedUser = (user: any, closePortal: () => void) => {
    setUserSearchTerm(user.name);
    setSelectedUser(user);
    closePortal();
  };

  const handleSelectedCompany = (company: any, closePortal: () => void) => {
    setCompanySearchTerm(company.name);
    setSelectedCompany(company);
    closePortal();
  };

  const users = useMemo(() => {
    return data?.searchWithMetadata.items.map(item => item.resource) ?? [];
  }, [data, error, loading]);

  const isEmpty = users.length === 0;
  const isEmptyAndLoading = isEmpty && loading;
  const isEmptyAndSearched = !loading && isEmpty && userSearchTerm !== "";

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {({ closeModal }) => (
        <>
          <ModalHeader title="Add Configuration" onClose={closeModal} />
          <FieldContainer>
            <Dropdown
              trigger={
                <InputField>
                  <FormLabel>User</FormLabel>
                  <FormInput
                    autoComplete="on"
                    placeholder="Search for users"
                    value={userSearchTerm}
                    onChange={e => setUserSearchTerm(e.target.value)}
                  />
                </InputField>
              }
            >
              {({ isOpen: isPortalOpen, closePortal }) =>
                isPortalOpen && (
                  <FilterDropdownContent align="left">
                    {isEmptyAndLoading && (
                      <FilterEmpty>Searching...</FilterEmpty>
                    )}
                    {isEmptyAndSearched && (
                      <FilterEmpty>{`No Results for ${userSearchTerm}.`}</FilterEmpty>
                    )}
                    {!isEmpty && !isEmptyAndSearched && (
                      <FilterButtonList>
                        {users
                          .filter(u => u.__typename === "User")
                          .map(u => {
                            const user = u as UserData;
                            const { email } = user;
                            const avatarUrl = user.avatar?.url;
                            const userName = user.name || undefined;
                            return (
                              <GenericDropdownItem
                                key={user.id}
                                title={`${user.id} | ${user.name}`}
                                onClick={() =>
                                  handleSelectedUser(user, closePortal)
                                }
                              >
                                <UserCardInDropdownItem
                                  email={email}
                                  imageUrl={avatarUrl}
                                  name={userName}
                                />
                              </GenericDropdownItem>
                            );
                          })}
                      </FilterButtonList>
                    )}
                  </FilterDropdownContent>
                )
              }
            </Dropdown>
          </FieldContainer>
          <FieldContainer>
            <Dropdown
              trigger={
                <InputField>
                  <FormLabel>Company</FormLabel>
                  <FormInput
                    autoComplete="on"
                    placeholder="Search for Company"
                    value={companySearchTerm}
                    onChange={e => setCompanySearchTerm(e.target.value)}
                  />
                </InputField>
              }
            >
              {({ isOpen: isPortalOpen, closePortal }) =>
                isPortalOpen && (
                  <FilterDropdownContent align="left">
                    {companySearchTerm && filteredCompanies.length === 0 && (
                      <FilterEmpty>{`No Results for ${companySearchTerm}.`}</FilterEmpty>
                    )}
                    <FilterButtonList>
                      {filteredCompanies.length > 0 &&
                        filteredCompanies.map(cleaningCompany => (
                          <GenericDropdownItem
                            key={cleaningCompany.id}
                            title={`${cleaningCompany.id} | ${cleaningCompany.name}`}
                            onClick={() =>
                              handleSelectedCompany(
                                cleaningCompany,
                                closePortal,
                              )
                            }
                          >
                            <span>{cleaningCompany.name}</span>
                          </GenericDropdownItem>
                        ))}
                    </FilterButtonList>
                  </FilterDropdownContent>
                )
              }
            </Dropdown>
          </FieldContainer>
          <ModalFooter>
            <AddQualityAuditConfigurationButton
              callBack={() => closeModal()}
              companyId={selectedCompany?.id}
              disabled={!(selectedUser?.id && selectedCompany?.id)}
              userId={selectedUser?.id}
            />
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}
